import {
    useEffect,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IPaletteSwitcherProps from "./type";
import type {
    PaletteSwitchVariant
} from "./type";
import stylesheet, {
    PALETTE_SWITCHER_DEFAULT_KEYS,
    PALETTE_SWITCHER_TITLE_KEYS,
    getPaletteSwitcherIconColor,
    getPaletteSwitcherDotColor,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    getSwitcherBorderColor,
    getSwitcherVariants
} from "../../helpers/preference";
import {
    warnOnce
} from "../../utils/development";
import PreferenceChevronIcon from "../../helpers/preference/menu/chevron";
import PreferenceForm from "../../helpers/preference/form";
import Button from "../button";

const PaletteSwitcher: FC<IPaletteSwitcherProps> = ({
    isShowTitle: isShowTitleProp,
    isWorkWithHighlightButton,
    isWorkWithNextShowSystem,
    variants: variantsProp,
    accessibilityLabel,
    isDisabled = false,
    customBorderColor,
    isShowNextVariant,
    variant = "tonal",
    type = "neutral",
    customIconColor,
    customTextColor,
    customLocalize,
    form = "cycle",
    customColor,
    customTheme,
    color,
    title,
    ...props
}) => {
    const {
        activePalette,
        paletteKeys
    } = NCoreUIKitTheme.useContext();

    const {
        activeTheme,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const paletteColors = useMemo(() => {
        return paletteKeys.reduce<Partial<Record<keyof NCoreUIKit.PaletteKey, string>>>((currentPaletteColors, paletteKey) => {
            const paletteState = NCoreUIKitTheme.prepare({
                activePalette: paletteKey,
                activeTheme
            });

            return {
                ...currentPaletteColors,
                [paletteKey]: paletteState.colors ? paletteState.colors.system.default.primary : undefined
            };
        }, {});
    }, [
        paletteKeys,
        activeTheme
    ]);

    const defaultVariants: Array<PaletteSwitchVariant> = PALETTE_SWITCHER_DEFAULT_KEYS.map((paletteKey) => ({
        title: localize(PALETTE_SWITCHER_TITLE_KEYS[paletteKey]),
        paletteKey
    }));

    const {
        accessibilityLabel: switcherAccessibilityLabel,
        variants: switcherVariants,
        displayVariant,
        nextKey
    } = getSwitcherVariants<"paletteKey", PaletteSwitchVariant>({
        isShowNextVariant: isShowNextVariant ?? isWorkWithNextShowSystem ?? false,
        title: localize("palette-switcher-title"),
        activeKey: activePalette,
        variants: variantsProp,
        keyName: "paletteKey",
        keys: paletteKeys,
        defaultVariants,
        localize
    });

    useEffect(() => {
        if(isWorkWithNextShowSystem === undefined) {
            return;
        }

        warnOnce({
            message: "PaletteSwitcher: \"isWorkWithNextShowSystem\" is deprecated and will be removed in the next major version. Use \"isShowNextVariant\" instead."
        });
    }, [
        isWorkWithNextShowSystem
    ]);

    useEffect(() => {
        if(isWorkWithHighlightButton === undefined) {
            return;
        }

        warnOnce({
            message: "PaletteSwitcher: \"isWorkWithHighlightButton\" is deprecated and will be removed in the next major version. The switcher is a tonal neutral Button by default."
        });
    }, [
        isWorkWithHighlightButton
    ]);

    const isShowTitle = isShowTitleProp ?? form !== "cycle";

    const isMenuForm = form === "menu";

    const displayTitle = displayVariant && isShowTitle ? displayVariant.title ?? String(displayVariant.paletteKey) : undefined;

    const currentTitle = title ?? displayTitle;

    const buttonVariant = isWorkWithHighlightButton || variant === "filled" ? "tonal" : variant;

    const isTitleInAccessibilityLabel = !currentTitle || switcherAccessibilityLabel.includes(currentTitle);

    const onPress = () => {
        if(!nextKey) {
            return;
        }

        NCoreUIKitTheme.switch({
            paletteKey: nextKey
        });
    };

    const handleSelect = (key: string) => {
        const selectedVariant = switcherVariants.find((variantItem) => String(variantItem.paletteKey) === key);

        if(!selectedVariant) {
            return;
        }

        NCoreUIKitTheme.switch({
            paletteKey: selectedVariant.paletteKey
        });
    };

    const getVariantIcon = (variantItem: PaletteSwitchVariant, isShowBorder: boolean): NCoreUIKitIcon => {
        const PaletteVariantIcon: NCoreUIKitIcon = ({
            customColor: buttonIconColor,
            color: iconColorKey,
            size
        }) => {
            const {
                dot: dotDynamicStyle
            } = useStyles({
                dotColor: getPaletteSwitcherDotColor({
                    paletteColor: paletteColors[variantItem.paletteKey],
                    iconColor: variantItem.iconColor,
                    buttonIconColor,
                    isDisabled,
                    colors
                }),
                isShowBorder: isShowBorder && !isDisabled,
                radiuses,
                borders,
                colors,
                size
            });

            if(variantItem.icon) {
                const VariantIcon = variantItem.icon;

                return <VariantIcon
                    customColor={getPaletteSwitcherIconColor({
                        isCustomIconColor: !!customIconColor || !!customTextColor,
                        iconColor: variantItem.iconColor,
                        buttonIconColor,
                        isDisabled,
                        colors
                    })}
                    color={iconColorKey}
                    size={size}
                />;
            }

            return <View
                style={[
                    stylesheet.dot,
                    dotDynamicStyle
                ]}
                aria-hidden={true}
            />;
        };

        return PaletteVariantIcon;
    };

    const renderIcon: NCoreUIKitIcon = (iconProps) => {
        const DisplayIcon = displayVariant ? getVariantIcon(displayVariant, !!currentTitle) : getVariantIcon({
            paletteKey: activePalette
        }, !!currentTitle);

        return DisplayIcon(iconProps);
    };

    const options = form === "cycle" ? [] : switcherVariants.map((variantItem) => ({
        title: variantItem.title ?? String(variantItem.paletteKey),
        icon: getVariantIcon(variantItem, true),
        key: String(variantItem.paletteKey)
    }));

    const trigger = <Button
        {...props}
        customColor={customColor || (displayVariant && displayVariant.backgroundColor ? displayVariant.backgroundColor : color)}
        accessibilityLabel={accessibilityLabel ?? (isTitleInAccessibilityLabel ? switcherAccessibilityLabel : undefined)}
        iconDirection={isMenuForm && currentTitle ? "end" : props.iconDirection}
        icon={isMenuForm && currentTitle ? PreferenceChevronIcon : renderIcon}
        customBorderColor={getSwitcherBorderColor({
            borderColor: displayVariant?.borderColor,
            customBorderColor,
            colors,
            color
        })}
        customIconColor={customIconColor}
        customTextColor={customTextColor}
        customTheme={customTheme}
        variant={buttonVariant}
        isDisabled={isDisabled}
        title={currentTitle}
        onPress={onPress}
        type={type}
    />;

    return <PreferenceForm
        size={props.size === "xSmall" ? "small" : props.size}
        title={localize("palette-switcher-title")}
        spreadBehaviour={props.spreadBehaviour}
        activeKey={String(activePalette)}
        customLocalize={customLocalize}
        customTheme={customTheme}
        isShowTitle={isShowTitle}
        onSelect={handleSelect}
        isDisabled={isDisabled}
        testID={props.testID}
        style={props.style}
        trigger={trigger}
        options={options}
        form={form}
    />;
};
export default PaletteSwitcher;
